import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { DiscountTypeEnum } from '../../../../enum/discount-type.enum';
import { CustomerState } from '../../../../stores/tunnel/model/customer-state';
import { KitchenExtraPricing } from '../../../../stores/tunnel/model/kitchen-extra-pricing';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { TunnelDataStore } from '../../../../stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from '../../../../stores/tunnel/tunnel-navigation.store';
import { KitchenService } from '../../../../services/configurators-2d/applications/kitchen.service';
import {
  FidelityCardPointSimulationDto,
  FidelityCardTypeEnum,
  GetCustomerFidelityInfoInput,
} from '../../../../services/fidelity/fidelity';
import { faBarcode } from '@fortawesome/free-solid-svg-icons';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { KitchenConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/kitchen-configuration-data';
import { StatisticService } from 'src/app/services/statistic.service';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { PaytweakRequestInput } from '../../../../services/fidelity/paytweak';
import { FidelityService } from '../../../../services/fidelity/fidelity.service';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { combineLatest, distinctUntilChanged, map, shareReplay, skip } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureSettingsService } from '../../../../services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-casto-kitchen-tunnel-validation',
  templateUrl: './kitchen-tunnel-validation.component.html',
})
export class KitchenTunnelValidationComponent extends BaseTunnelObject implements OnInit, OnDestroy {
  // Injects
  protected tunnelDataStore = inject(TunnelDataStore);
  protected tunnelNavigationStore = inject(TunnelNavigationStore);
  protected kitchenService = inject(KitchenService);
  private readonly modalService = inject(ModalService);
  private readonly statisticService = inject(StatisticService);
  private readonly fidelityService = inject(FidelityService);
  protected readonly featureSettingsService = inject(FeatureSettingsService);
  private destroyRef = inject(DestroyRef);

  // Observables de base
  protected currentPricing$ = this.tunnelDataStore.getCurrentPricing();
  protected currentDiscount$ = this.tunnelDataStore.getCurrentDiscount();
  protected currentInstallation$ = this.tunnelDataStore.getCurrentInstallation();
  protected currentWithInstallation$ = this.tunnelDataStore.getCurrentWithInstallation();
  protected currentFunding$ = this.tunnelDataStore.getCurrentFunding();
  protected currentCustomer$ = this.tunnelDataStore.getCurrentCustomer();
  protected fidelityLoading$ = this.tunnelDataStore.getFidelityLoading();
  protected currentComplementaryProduct$ = this.tunnelDataStore.getCurrentComplementaryProduct();

  protected kitchenConfiguration$ = this.tunnelDataStore.getConfigurationData().pipe(
    map(configurationData => configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData),
    shareReplay(1)
  );

  // Observables dérivés
  protected discountNumberValue$ = this.currentDiscount$.pipe(
    map(discount => {
      switch (discount) {
        case DiscountTypeEnum.Discount0:
          return 0;
        case DiscountTypeEnum.Discount5:
          return 5;
        case DiscountTypeEnum.Discount10:
          return 10;
        case DiscountTypeEnum.Discount20:
          return 20;
        default:
          return 0;
      }
    }),
    shareReplay(1)
  );

  protected displayDiscount$ = this.discountNumberValue$.pipe(map(value => value > 0));

  protected extraPricing$ = this.currentPricing$.pipe(map(pricing => pricing?.extraPricing as KitchenExtraPricing));

  protected isCustomWorkbenchOnly$ = this.extraPricing$.pipe(
    map(extra => extra?.totalWorkbenchPlan.actualAmount > 0 && !extra?.totalInstallation && !extra?.totalDelivery)
  );

  protected isCustomWorkbench$ = this.extraPricing$.pipe(
    map(
      extra =>
        extra?.totalWorkbenchPlan.actualAmount > 0 &&
        (extra?.totalInstallation !== undefined || extra?.totalDelivery !== undefined)
    )
  );

  protected currentFidelity$ = combineLatest([this.currentPricing$, this.currentWithInstallation$]).pipe(
    map(([pricing, withInstallation]) => {
      if (pricing?.fidelity) {
        return pricing.fidelity[withInstallation ? 0 : 1];
      }
      return undefined;
    }),
    shareReplay(1)
  );

  protected funding$ = combineLatest([this.currentFunding$, this.currentInstallation$]).pipe(
    map(([funding, installation]) => {
      if (!funding || !installation) return null;
      return funding[installation.withInstallation ? 'fundingWithInstallation' : 'fundingWithoutInstallation'];
    }),
    shareReplay(1)
  );

  protected isCardExpired$ = this.currentCustomer$.pipe(
    map(customer => {
      if (!customer?.fidelity?.expiredDate) return false;

      const today = new Date();
      const dateToNotice = new Date();
      const nbDaysNoticeEndSubscriptionPaidCard = 30;

      dateToNotice.setTime(
        customer.fidelity.expiredDate.getTime() - nbDaysNoticeEndSubscriptionPaidCard * 24 * 60 * 60 * 1000
      );

      return today > dateToNotice;
    })
  );

  protected premiumCardEarnedCoupons$ = this.currentFidelity$.pipe(
    map(fidelity => {
      let count = 0;
      fidelity?.premiumCardCoupons?.forEach(coupon => {
        count += coupon.count;
      });
      return count;
    })
  );

  // ViewModel complet
  protected validationViewModel$ = combineLatest({
    currentPricing: this.currentPricing$,
    currentDiscount: this.currentDiscount$,
    currentInstallation: this.currentInstallation$,
    currentWithInstallation: this.currentWithInstallation$,
    currentFunding: this.currentFunding$,
    currentCustomer: this.currentCustomer$,
    fidelityLoading: this.fidelityLoading$,
    kitchenConfiguration: this.kitchenConfiguration$,
    discountNumberValue: this.discountNumberValue$,
    displayDiscount: this.displayDiscount$,
    extraPricing: this.extraPricing$,
    isCustomWorkbenchOnly: this.isCustomWorkbenchOnly$,
    isCustomWorkbench: this.isCustomWorkbench$,
    currentFidelity: this.currentFidelity$,
    funding: this.funding$,
    isCardExpired: this.isCardExpired$,
    premiumCardEarnedCoupons: this.premiumCardEarnedCoupons$,
    currentComplementaryProduct: this.currentComplementaryProduct$,
  });

  // Propriétés locales
  protected loading: boolean = false;
  protected refreshLoading: boolean = false;

  // Constantes
  protected readonly faBarcode = faBarcode;
  protected readonly FidelityCardTypeEnum = FidelityCardTypeEnum;

  ngOnInit(): void {
    super.ngOnInit();
    this.sendAccessStatistic();
    this.computeFidelityPoint();
  }

  /**
   * Envoie la statistique d'accès à la page
   */
  private sendAccessStatistic(): void {
    const state = this.tunnelDataStore.getCurrentStateValue();
    const kitchenConfig = state.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;

    this.statisticService.sendStatistic(
      StatisticTypeEnum.VALIDATION_PER_PROJECT_COUNT,
      1,
      CONFIG_CONTEXT.KITCHEN,
      kitchenConfig?.configurationId
    );
  }

  protected subscribeToObservable() {
    this.currentWithInstallation$
      .pipe(
        distinctUntilChanged(),
        skip(1), // Ignore la première valeur (initialisation)
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.loading) {
          this.updatePricingFunding();
        }
      });
  }

  protected async setWithInstallation(withInstallation: boolean) {
    try {
      this.loading = true;
      await this.kitchenService.updateWithInstallation(withInstallation);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de withInstallation:', error);
    } finally {
      this.loading = false;
    }

    this.tunnelDataStore.setWithInstallation(withInstallation);
  }

  openDetail() {
    const state = this.tunnelDataStore.getCurrentStateValue();
    const kitchenConfig = state.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;

    this.modalService.display(MODAL.INSTALLATION_DETAIL, {
      installationDetails: state.currentInstallation?.detailInstallation,
      bom: kitchenConfig?.bom,
    });
  }

  openDetailWorkbench() {
    const state = this.tunnelDataStore.getCurrentStateValue();
    const kitchenConfig = state.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;
    const extraPricing = state.currentPricing?.extraPricing as KitchenExtraPricing;

    this.modalService.display(MODAL.WORKBENCH_DETAIL, {
      workbenchResume: extraPricing?.kitchenWorkbenchConfigurations,
      bom: kitchenConfig?.bom,
      discountValue: state.currentDiscount,
    });
  }

  private async updatePricingFunding() {
    try {
      this.loading = true;
      await this.computePricing();
      await this.computeFunding();
    } catch (error) {
      logError('Erreur lors de la mise à jour du pricing et funding :', error);
    } finally {
      this.loading = false;
    }
  }

  private async computeFidelityPoint() {
    const state = this.tunnelDataStore.getCurrentStateValue();

    try {
      await this.kitchenService.computeFidelityPoint({
        amount: state.currentPricing?.totalWithoutFunding.actualAmount,
        customerId: state.currentCustomer?.userData.kfId,
      });
    } catch (error) {
      console.error('Erreur lors du calcul des points de fidélité:', error);
    }
  }

  private async computePricing() {
    await this.kitchenService.computePricing();
  }

  private async computeFunding() {
    const state = this.tunnelDataStore.getCurrentStateValue();
    const kitchenConfig = state.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;

    await this.kitchenService.computeFunding({
      tunnelConfigurationId: kitchenConfig?.configurationId,
      downPaymentAmount: state.currentFunding?.downPayment,
      shouldRecalculateFinancing: true,
    });
  }

  private async createFidelityCard(input: { withPremium: boolean }): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    const state = this.tunnelDataStore.getCurrentStateValue();

    this.modalService.display(MODAL.CUSTOMER_INFO_FORM, {
      defaultData: {
        customer: state.currentCustomer?.userData,
        address: state.currentCustomer?.userData.address,
        marketingConsent: state.currentCustomer?.userData.marketingConsent,
      },
      upgrade: input.withPremium,
      statisticType: input.withPremium
        ? StatisticTypeEnum.NEW_PREMIUM_CARD_VALIDATION
        : StatisticTypeEnum.NEW_FREE_CARD_VALIDATION,
    });
  }

  protected async createPremiumCard() {
    await this.createFidelityCard({
      withPremium: true,
    });
  }

  protected async createFreeCard() {
    await this.createFidelityCard({
      withPremium: false,
    });
  }

  private buildPaytweakRequestInput(
    customer: CustomerState,
    fidelity: FidelityCardPointSimulationDto
  ): PaytweakRequestInput {
    return {
      customerId: customer.userData.kfId,
      cardNumber: fidelity.cardNumber,
      firstname: customer.userData.firstname,
      lastname: customer.userData.lastname,
      address: customer.userData.address.line1,
      postalCode: customer.userData.address.postalCode,
      city: customer.userData.address.city,
      country: customer.userData.address.country,
      phone: customer.userData.phoneNumber,
      email: customer.userData.email,
    };
  }

  private async showPaytweakQrCode(): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    const state = this.tunnelDataStore.getCurrentStateValue();

    // Récupère la fidélité actuelle de manière synchrone
    const currentFidelity = state.currentPricing?.fidelity?.[state.currentInstallation?.withInstallation ? 0 : 1];

    if (!currentFidelity || !state.currentCustomer) {
      console.error("Impossible d'afficher le QR code : données manquantes");
      return;
    }

    const input = this.buildPaytweakRequestInput(state.currentCustomer, currentFidelity);

    this.modalService.display(MODAL.FIDELITY_PAYMENT_QRCODE, {
      paytweakRequestInput: input,
      onCloseCallback: undefined,
    });
  }

  private async postRenewPremiumCard(): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    const state = this.tunnelDataStore.getCurrentStateValue();
    const kfId = state.currentCustomer?.userData.kfId;

    if (kfId == null) {
      logError('Impossible de renouveler la carte : KF ID manquant');
      return;
    }

    const input: GetCustomerFidelityInfoInput = { idKingfisher: kfId };

    try {
      await this.fidelityService.postRenewPremiumCard(input);
    } catch (error) {
      logError('Erreur lors du renouvellement de la carte premium :', error);
    } finally {
      await this.showPaytweakQrCode();
    }
  }

  protected async updatePremiumCard() {
    await this.postRenewPremiumCard();
  }

  protected async refreshFidelity(): Promise<void> {
    const state = this.tunnelDataStore.getCurrentStateValue();

    try {
      this.refreshLoading = true;
      await this.tunnelDataStore.loadCurrentCustomer(state.currentCustomer?.userData);
    } catch (error) {
      logError('Erreur lors du rafraîchissement de la fidélité :', error);
    } finally {
      this.refreshLoading = false;
    }
  }

  protected async getPaymentLink() {
    await this.showPaytweakQrCode();
  }

  protected fidelityEnabled(): boolean {
    return this.featureSettingsService.fidelityEnabled;
  }

}
